import { useState } from 'react';
import { cropAspectRatios } from '@ciancio/shared';

const versions = [
  { id: 'original', label: 'Original' },
  { id: 'customerEdited', label: 'Customer Edit' },
];

export const ReviewImageCard = ({ image, savingVersion, onVersionChange }) => {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const previewUrl = image.versionUrls?.[image.printVersion] || image.versionUrls?.customerEdited || image.versionUrls?.original;
  const editSummary = getEditSummary(image.editState);
  const printSize = getPrintSize(image.editState);
  const metadataId = `metadata-${image.id}`;

  return (
    <div className="col-12 col-sm-6 col-lg-4 col-xl-3 p-2">
      <div className="review-image-card bg-black text-white code-preview h-100">
        <div className="review-image-card-preview">
          <ImagePreview image={image} previewUrl={previewUrl} savingVersion={savingVersion} />
        </div>

        <div className="review-image-card-body m-0">
          <div className="review-image-title-block">
            <div className="review-image-title-content">
              <span className="review-image-label">Filename</span>
              <h5 className="review-image-title">{image.filename}</h5>
              <p className="m-0"><strong>Print size:</strong> {printSize}</p>
            </div>
            <button
              className="btn bg-beige review-metadata-toggle"
              type="button"
              aria-controls={metadataId}
              aria-expanded={metadataOpen}
              onClick={() => setMetadataOpen((current) => !current)}
            >
              <i className={metadataOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} />
            </button>
          </div>

          <div id={metadataId} className={metadataOpen ? 'review-metadata-collapse collapse show' : 'review-metadata-collapse collapse'}>
            <div className="row g-2 mb-2">
              <MetadataItem label="Type" value={image.contentType || 'image'} />
              <MetadataItem label="Size" value={formatBytes(image.size)} />
            </div>

            <div className="review-edit-summary bg-light text-dark p-2 mb-2">
              <p className="m-0"><strong>Customer edit:</strong> {image.customerEditedPath ? 'Available' : 'None'}</p>
              <p className="m-0"><strong>Crop:</strong> {editSummary.crop}</p>
              <p className="m-0"><strong>Colors:</strong> {editSummary.colors}</p>
            </div>
          </div>
        </div>

        <div className="review-image-card-actions">
          <div className="row m-0">
            <div className="col-10 p-0 pe-2">
              <select className="form-select" value={image.printVersion} onChange={(event) => onVersionChange(image.id, event.target.value)} disabled={savingVersion}>
                {versions.map((version) => (
                  <option key={version.id} value={version.id} disabled={!image.versionUrls?.[version.id]}>
                    {version.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-2 p-0">
              {previewUrl && (
                <a className="btn btn-light w-100 align-content-center" href={previewUrl} download={getDownloadName(image)} target="_blank" rel="noreferrer">
                  <i className="bi bi-download" />
                </a>
              )}
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}

const ImagePreview = ({ image, previewUrl, savingVersion }) => {
  if (!previewUrl) {
    return (
      <div className="review-image-preview review-image-preview-empty d-flex align-items-center justify-content-center">
        Preview unavailable
      </div>
    );
  }

  return (
    <div className="review-image-preview-shell">
      <img className="review-image-preview w-100" src={previewUrl} alt={`${image.filename} ${image.printVersion}`} />
      {savingVersion && (
        <div className="review-version-loading d-flex align-items-center justify-content-center">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

const MetadataItem = ({ label, value }) => (
  <div className="col-6">
    <div className="review-metadata-item">
      <span className="review-image-label">{label}</span>
      <p className="m-0">{value}</p>
    </div>
  </div>
);

function getDownloadName(image) {
  const extension = image.filename?.includes('.') ? '' : '.jpg';
  return `${image.printVersion || 'selected'}-${image.filename || image.id}${extension}`;
}

function formatBytes(bytes = 0) {
  if (!bytes) {
    return 'Unknown size';
  }

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getEditSummary(editState = {}) {
  const crop = editState.crop
    ? `${getPrintSize(editState)} at ${Math.round(editState.crop.size || 100)}%`
    : 'Default';

  if (editState.mode === 'advanced') {
    const { saturation = 100, hue = 0, brightness = 100 } = editState.adjustments || {};
    return {
      crop,
      colors: `Saturation ${saturation}%, Hue ${hue}deg, Brightness ${brightness}%`,
    };
  }

  return {
    crop,
    colors: editState.filter || 'normal',
  };
}

function getPrintSize(editState = {}) {
  const aspectId = editState.crop?.aspect;
  const printSize = cropAspectRatios.find((ratio) => ratio.id === aspectId);

  return printSize?.label || '5 x 5 inches';
}
